import { Outlet, useNavigate } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { createLogger } from "@ict/shared";
import { useAuth } from "@/hooks/useAuth";
import { useAutoSync } from "@/hooks/useAutoSync";
import { SyncButton } from "@/components/ui/SyncButton";
import { TestCompanyBanner } from "@/components/layout/TestCompanyBanner";
import { FloatingContactButton } from "@/components/contact/FloatingContactButton";

const logger = createLogger({ module: "Layout" });

interface LayoutProps {
  /** 메인 헤더 표시 여부 (기본값: true) */
  showHeader?: boolean;
}

export function Layout({ showHeader = true }: LayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();

  // 전역 자동 동기화 (온라인 전환 시 트리거)
  // 동기화 완료 후 모든 활성 쿼리를 refetch하여 UI 갱신
  useAutoSync({
    onSyncComplete: async () => {
      try {
        // reFetchObservableQueries는 현재 관찰 중인 모든 쿼리를 강제로 refetch
        await apolloClient.reFetchObservableQueries();
      } catch (err) {
        logger.error("refetch 실패:", err);
      }
    },
  });

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      // 로그아웃 성공/실패 여부와 관계없이 로그인 페이지로 이동
      navigate("/login");
    }
  };

  return (
    <div className="flex h-full flex-col bg-slate-50">
      {/* Safe Area 상단 여백 (Status Bar 영역) */}
      <div className="safe-area-top bg-slate-900" />

      {/* 헤더 - Industrial Minimal (showHeader가 true일 때만 표시) */}
      {showHeader && (
        <header className="sticky top-0 z-50 border-b border-slate-800/10 bg-slate-900 px-4 py-2.5">
          <div className="flex items-center justify-between">
            {/* 좌측: 로고 + 회사명 */}
            <div className="flex items-center gap-2">
              {/* 로고 */}
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-500">
                <svg
                  className="h-5 w-5 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  />
                </svg>
              </div>
              {/* 회사명 */}
              {user?.companyName && (
                <span className="max-w-[120px] truncate text-sm font-medium text-slate-300">
                  {user.companyName}
                </span>
              )}
            </div>

            {/* 우측 액션 영역 */}
            <div className="flex items-center gap-1">
              <SyncButton />
              <div className="mx-1 h-4 w-px bg-slate-700" />
              <span className="text-sm text-slate-300">{user?.name}</span>
              {/* 계정 설정 */}
              <button
                onClick={() => navigate("/settings")}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-700 active:bg-slate-600"
                aria-label="설정"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>
              {/* 로그아웃 */}
              <button
                onClick={handleLogout}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-700 active:bg-slate-600"
                aria-label="로그아웃"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
              </button>
            </div>
          </div>
        </header>
      )}

      {/* 테스트 회사(데모 모드) 배너 - 헤더가 표시될 때만 */}
      {showHeader && <TestCompanyBanner />}

      {/* 메인 콘텐츠 */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>

      {/* 플로팅 문의하기 버튼 */}
      <FloatingContactButton />

      {/* Safe Area 하단 여백 (Navigation Bar 겹침 방지) */}
      <div className="safe-area-bottom bg-slate-900" />
    </div>
  );
}
